import { useCallback, useMemo, useState } from 'react';
import BigNumber from 'bignumber.js';
import { Address } from 'viem';

import { useConnection } from 'modules/auth/hooks/useConnection';
import { ZERO } from 'modules/common/const';

import { useGetAllowanceQuery } from '../actions/getAllowance';
import { useSetAllowanceMutation } from '../actions/setAllowance';

interface IUseStakeApproveParams {
  amount: BigNumber;
  poolAddress: Address;
}

interface IUseStakeApproveResult {
  amount: BigNumber;
  onApprove: () => void;
  isApproved: boolean;
  isLoading: boolean;
  isDisabled: boolean;
}

export function useApprove({
  amount,
  poolAddress,
}: IUseStakeApproveParams): IUseStakeApproveResult {
  const [isAllowanceLoading, setIsAllowanceLoading] = useState(false);

  const [setAllowance, { isLoading: isSetAllowanceLoading }] =
    useSetAllowanceMutation();
  const { isConnected, connectionKey, cacheTime } = useConnection();

  const {
    data: allowanceData,
    isLoading: isGetAllowanceLoading,
    refetch,
  } = useGetAllowanceQuery(
    {
      poolAddress,
      connectionKey,
    },
    {
      skip: !isConnected || !poolAddress,
      refetchOnMountOrArgChange: cacheTime,
      pollingInterval: cacheTime,
    },
  );

  const onApprove = useCallback(() => {
    setIsAllowanceLoading(true);

    void setAllowance({ amount, poolAddress })
      .then(() => refetch())
      .finally(() => setIsAllowanceLoading(false));
  }, [setAllowance, amount, poolAddress, refetch]);

  const isDisabled = useMemo(
    () => amount.isZero() || !isConnected,
    [amount, isConnected],
  );

  const isApproved =
    !!allowanceData &&
    poolAddress === allowanceData.spender &&
    amount.isLessThanOrEqualTo(allowanceData.allowance);

  return {
    amount: allowanceData?.allowance || ZERO,
    isApproved,
    isDisabled,
    isLoading:
      isAllowanceLoading || isSetAllowanceLoading || isGetAllowanceLoading,
    onApprove,
  };
}
